import { getMeetingsResponseSchema, meetingSchema, userMeetingStatisticsSchema, getTranscriptsResponseSchema, meetingSummarySchema, meetingSummaryResponseSchema, getActionItemsResponseSchema, actionItemResponseSchema, getProjectActionItemsResponseSchema, getAgreementsResponseSchema, meetingAgreementSchema, generateAgreementsResponseSchema, getAgreementContradictionsResponseSchema, getDocumentContradictionsResponseSchema, recurringSeriesResponseSchema, SUMMARY_SEGMENTS_NOT_READY, generateEnqueueResponseSchema, taskEnqueueResponseSchema, taskInitiatedResponseSchema, getProjectParticipantsResponseSchema, assignSpeakerResponseSchema, } from '@consigli/types';
import { z } from 'zod';
import { createParams, createTransformer, mapToSnakeCase, } from './shared/base-api';
import { pollTask } from './shared/poll-task';
const HTTP_STATUS_SERVICE_UNAVAILABLE = 503;
const outlookItemErrorSchema = z.object({
    id: z.string(),
    kind: z.enum(['event', 'series']),
    error: z.string(),
});
// Both import endpoints return the same snake_cased payload; the auto-import
// variant just makes every field optional. Share the field shape and transform
// so the two schemas can't drift apart.
const toImportOutlookMeetingsResponse = (data) => ({
    importedMeetingCount: data.imported_meeting_count,
    eventCount: data.event_count,
    seriesCount: data.series_count,
    transcriptFetchesEnqueued: data.transcript_fetches_enqueued,
    errors: data.errors,
    message: data.message,
});
const importOutlookMeetingsResponseSchema = z
    .object({
    imported_meeting_count: z.number(),
    event_count: z.number(),
    series_count: z.number(),
    transcript_fetches_enqueued: z.number(),
    errors: z.array(outlookItemErrorSchema).default([]),
    message: z.string().default(''),
})
    .passthrough()
    .transform(toImportOutlookMeetingsResponse);
const autoImportTaggedResultSchema = z
    .object({
    imported_meeting_count: z.number().optional().default(0),
    event_count: z.number().optional().default(0),
    series_count: z.number().optional().default(0),
    transcript_fetches_enqueued: z.number().optional().default(0),
    errors: z.array(outlookItemErrorSchema).optional().default([]),
    message: z.string().optional().default(''),
})
    .passthrough()
    .transform(toImportOutlookMeetingsResponse);
// Helper to build MS Graph headers for meeting assistant endpoints
// Note: Authorization header is already added by baseQuery
const buildMeetingSecretaryHeaders = (params) => ({
    'Ms-Access-Token': `Bearer ${params.msAccessToken}`,
    'X-User-Email': params.userEmail,
    'X-User-Id': params.userId,
});
const serializeError = (error) => {
    if (!error) {
        return {
            status: 'CUSTOM_ERROR',
            data: 'An unknown error occurred',
        };
    }
    const err = error;
    return {
        status: err.status ?? 'CUSTOM_ERROR',
        data: err.data ?? err.message ?? 'An error occurred',
    };
};
const parseTaskInitiatedResponse = (data) => {
    const parsed = taskInitiatedResponseSchema.safeParse(data);
    if (!parsed.success) {
        return {
            ok: false,
            error: serializeError({
                status: 'CUSTOM_ERROR',
                data: `Invalid task initiation response: ${parsed.error.message}`,
            }),
        };
    }
    return { ok: true, task_id: parsed.data.task_id };
};
export async function createMeetingSummaryFn(params, signal, baseQuery, baseUrl) {
    const headers = buildMeetingSecretaryHeaders(params);
    const summaryUrl = `${baseUrl}/meetings/${params.meetingId}/summary?project_id=${params.projectId}`;
    const fetchSummary = async () => {
        const result = await baseQuery({ url: summaryUrl, method: 'GET', headers });
        if (result.error)
            return { error: serializeError(result.error) };
        const transformer = createTransformer(meetingSummarySchema, 'CreateMeetingSummary');
        return { data: transformer(result.data) };
    };
    // POST to the new async generate endpoint
    const generateUrl = `${baseUrl}/meetings/${params.meetingId}/summary/generate?project_id=${params.projectId}`;
    const genResult = await baseQuery({ url: generateUrl, method: 'POST', headers });
    if (genResult.error) {
        return { error: serializeError(genResult.error) };
    }
    const enqueue = generateEnqueueResponseSchema.safeParse(genResult.data);
    if (!enqueue.success) {
        return {
            error: serializeError({
                status: 'CUSTOM_ERROR',
                data: `Invalid summary/generate response: ${enqueue.error.message}`,
            }),
        };
    }
    // Summary already existed — fetch it directly
    if (enqueue.data.already_generated) {
        return fetchSummary();
    }
    // No task queued and not already generated — unexpected
    if (!enqueue.data.task_id) {
        return {
            error: serializeError({
                status: 'CUSTOM_ERROR',
                data: 'summary/generate returned no task_id and already_generated was false',
            }),
        };
    }
    // Poll until task completes, then fetch the summary
    const statusUrl = `${baseUrl}/tasks/${enqueue.data.task_id}?project_id=${params.projectId}`;
    try {
        await pollTask(() => baseQuery({ url: statusUrl, method: 'GET', headers }), { parse: (_) => null }, { signal });
    }
    catch (err) {
        const httpErr = err;
        if (httpErr?.status === HTTP_STATUS_SERVICE_UNAVAILABLE) {
            return {
                error: serializeError({
                    status: HTTP_STATUS_SERVICE_UNAVAILABLE,
                    data: SUMMARY_SEGMENTS_NOT_READY,
                }),
            };
        }
        return { error: serializeError(err) };
    }
    return fetchSummary();
}
// No `Content-Type` is set: the browser adds the multipart boundary for a FormData body.
export const buildTranscriptUploadRequest = (params, baseUrl) => {
    const body = new FormData();
    body.append('file', params.file);
    return {
        url: `${baseUrl}/meetings/${params.meetingId}/transcripts/upload?project_id=${params.projectId}`,
        method: 'POST',
        body,
    };
};
// The audio run downloads the recording and transcribes it, which is slower than the other
// transcript tasks. The backend task timeout is 300s, so the default 5 minute poll limit would
// give up at the same moment; this one outlasts it and backs off to spare the API.
const REGENERATE_WITH_AUDIO_POLL_OPTIONS = {
    intervalMs: 3000,
    backoffMultiplier: 1.5,
    maxIntervalMs: 15_000,
    timeoutMs: 360_000,
};
export async function regenerateTranscriptWithAudioFn(params, signal, baseQuery, baseUrl) {
    const headers = buildMeetingSecretaryHeaders(params);
    const url = `${baseUrl}/meetings/${params.meetingId}/transcripts/regenerate-with-audio?project_id=${params.projectId}&join_url=${encodeURIComponent(params.joinUrl)}`;
    const enqueue = await baseQuery({ url, method: 'POST', headers });
    if (enqueue.error) {
        return { error: serializeError(enqueue.error) };
    }
    const task = parseTaskInitiatedResponse(enqueue.data);
    if (!task.ok) {
        return { error: task.error };
    }
    const statusUrl = `${baseUrl}/tasks/${task.task_id}?project_id=${params.projectId}`;
    try {
        await pollTask(() => baseQuery({ url: statusUrl, method: 'GET', headers }), { parse: (_) => null }, { signal, ...REGENERATE_WITH_AUDIO_POLL_OPTIONS });
    }
    catch (err) {
        return { error: serializeError(err) };
    }
    // The outcome is read by fetching the transcripts again: a run that found no usable
    // recording succeeds without changing anything.
    return { data: null };
}
export const meetingApiEndpoints = (builder, meetingApiBaseUrl) => ({
    getMeetings: builder.query({
        query: (params) => ({
            url: `${meetingApiBaseUrl}/meetings`,
            method: 'GET',
            params: createParams(mapToSnakeCase({ ...params })),
        }),
        providesTags: (_result, _error, params) => [
            'Meetings',
            {
                type: 'Meetings',
                id: `${params.projectId}-${params.startDate || 'all'}-${params.endDate || 'all'}`,
            },
        ],
        transformResponse: createTransformer(getMeetingsResponseSchema, 'GetMeetings'),
    }),
    getUserMeetingStatistics: builder.query({
        query: (params) => ({
            url: `${meetingApiBaseUrl}/meetings/statistics`,
            method: 'GET',
            params: createParams(mapToSnakeCase(params)),
        }),
        providesTags: (_result, _error, params) => [
            { type: 'Meetings', id: `statistics-${params.projectId}` },
        ],
        transformResponse: createTransformer(userMeetingStatisticsSchema, 'GetUserMeetingStatistics'),
    }),
    getMeetingTranscripts: builder.query({
        queryFn: async (params, queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/${params.meetingId}/transcripts?join_url=${encodeURIComponent(params.joinUrl)}&project_id=${params.projectId}`;
            const headers = buildMeetingSecretaryHeaders(params);
            const result = await baseQuery({ url, method: 'GET', headers });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            const taskCheck = taskInitiatedResponseSchema.safeParse(result.data);
            if (taskCheck.success) {
                const statusUrl = `${meetingApiBaseUrl}/tasks/${taskCheck.data.task_id}?project_id=${params.projectId}`;
                const transform = createTransformer(getTranscriptsResponseSchema, 'GetMeetingTranscripts');
                let polled;
                try {
                    polled = await pollTask(() => baseQuery({ url: statusUrl, method: 'GET', headers }), { parse: transform }, { signal: queryApi.signal, intervalMs: 3000 });
                }
                catch (err) {
                    return { error: serializeError(err) };
                }
                return { data: polled };
            }
            // 200: transcripts already in DB (cached or "unavailable" empty marker).
            const transformer = createTransformer(getTranscriptsResponseSchema, 'GetMeetingTranscripts');
            return { data: transformer(result.data) };
        },
        providesTags: (_result, _error, params) => {
            // Use calendarEventId if available for recurring meetings to ensure unique cache entries
            const cacheId = params.calendarEventId
                ? `${params.meetingId}-${params.calendarEventId}`
                : params.meetingId;
            return [{ type: 'MeetingTranscripts', id: cacheId }];
        },
    }),
    uploadMeetingTranscript: builder.mutation({
        query: (params) => buildTranscriptUploadRequest(params, meetingApiBaseUrl),
        // Failures carry `{detail: {error}}`. The shared 403 toast would print that object.
        extraOptions: { suppressErrorToast: true },
        transformResponse: createTransformer(getTranscriptsResponseSchema, 'UploadMeetingTranscript'),
        // `MeetingTranscripts` is deliberately left alone: the transcripts are read through a lazy
        // query the caller triggers itself, and invalidating would refetch it with stale arguments.
        invalidatesTags: ['Meetings'],
    }),
    regenerateTranscriptWithAudio: builder.mutation({
        queryFn: async (params, queryApi, _extraOptions, baseQuery) => regenerateTranscriptWithAudioFn(params, queryApi.signal, baseQuery, meetingApiBaseUrl),
        extraOptions: { suppressErrorToast: true },
        // The backend deletes everything generated from the text-only segments, including the
        // provisional document contradictions (cached per package, so by type).
        invalidatesTags: (_result, _error, params) => [
            'Meetings',
            'DocumentContradictions',
            { type: 'MeetingSummary', id: params.meetingId },
            { type: 'MeetingActionItems', id: params.meetingId },
            { type: 'MeetingActionItems', id: `project-${params.projectId}` },
            { type: 'MeetingAgreements', id: params.meetingId },
            { type: 'MeetingContradictions', id: params.meetingId },
        ],
    }),
    getMeetingSummary: builder.query({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/${params.meetingId}/summary?project_id=${params.projectId}`;
            const result = await baseQuery({
                url,
                method: 'GET',
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            const transformer = createTransformer(meetingSummaryResponseSchema, 'GetMeetingSummary');
            return { data: transformer(result.data) };
        },
        providesTags: (_result, _error, params) => [{ type: 'MeetingSummary', id: params.meetingId }],
    }),
    createMeetingSummary: builder.mutation({
        queryFn: (params, queryApi, _extraOptions, baseQuery) => createMeetingSummaryFn(params, queryApi.signal, baseQuery, meetingApiBaseUrl),
        invalidatesTags: (_result, _error, params) => [
            { type: 'MeetingSummary', id: params.meetingId },
        ],
    }),
    updateMeetingSummary: builder.mutation({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/${params.meetingId}/summary?project_id=${params.projectId}`;
            const result = await baseQuery({
                url,
                method: 'PATCH',
                body: mapToSnakeCase(params.body),
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            const transformer = createTransformer(meetingSummarySchema, 'UpdateMeetingSummary');
            return { data: transformer(result.data) };
        },
        invalidatesTags: (_result, _error, params) => [
            { type: 'MeetingSummary', id: params.meetingId },
        ],
    }),
    getMeetingActionItems: builder.query({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/${params.meetingId}/action-items?project_id=${params.projectId}`;
            const result = await baseQuery({
                url,
                method: 'GET',
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            const transformer = createTransformer(getActionItemsResponseSchema, 'GetMeetingActionItems');
            return { data: transformer(result.data) };
        },
        providesTags: (_result, _error, params) => [
            { type: 'MeetingActionItems', id: params.meetingId },
        ],
    }),
    getProjectActionItems: builder.query({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/projects/${params.projectId}/action-items`;
            const result = await baseQuery({
                url,
                method: 'GET',
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            const transformer = createTransformer(getProjectActionItemsResponseSchema, 'GetProjectActionItems');
            return { data: transformer(result.data) };
        },
        providesTags: (_result, _error, params) => [
            { type: 'MeetingActionItems', id: `project-${params.projectId}` },
        ],
    }),
    generateMeetingActionItems: builder.mutation({
        queryFn: async (params, queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/${params.meetingId}/action-items/generate?project_id=${params.projectId}`;
            const genResult = await baseQuery({
                url,
                method: 'POST',
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (genResult.error) {
                return { error: serializeError(genResult.error) };
            }
            const taskInit = parseTaskInitiatedResponse(genResult.data);
            if (!taskInit.ok) {
                return { error: taskInit.error };
            }
            const statusUrl = `${meetingApiBaseUrl}/tasks/${taskInit.task_id}?project_id=${params.projectId}`;
            let result;
            try {
                result = await pollTask(() => Promise.resolve(baseQuery({
                    url: statusUrl,
                    method: 'GET',
                    headers: buildMeetingSecretaryHeaders(params),
                })), getActionItemsResponseSchema, { signal: queryApi.signal });
            }
            catch (err) {
                return { error: serializeError(err) };
            }
            return { data: result };
        },
        invalidatesTags: (_result, _error, params) => [
            { type: 'MeetingActionItems', id: params.meetingId },
            { type: 'MeetingActionItems', id: `project-${params.projectId}` },
        ],
    }),
    updateActionItem: builder.mutation({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/${params.meetingId}/action-items/${params.actionItemId}?project_id=${params.projectId}`;
            const result = await baseQuery({
                url,
                method: 'PATCH',
                body: mapToSnakeCase(params.body),
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            const transformer = createTransformer(actionItemResponseSchema, 'UpdateActionItem');
            return { data: transformer(result.data) };
        },
        invalidatesTags: (_result, _error, params) => [
            { type: 'MeetingActionItems', id: params.meetingId },
            { type: 'MeetingActionItems', id: `project-${params.projectId}` },
        ],
    }),
    assignSegmentSpeaker: builder.mutation({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/${params.meetingId}/segments/${params.segmentId}/speaker?project_id=${params.projectId}`;
            const result = await baseQuery({
                url,
                method: 'PATCH',
                body: mapToSnakeCase(params.body),
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            const transformer = createTransformer(assignSpeakerResponseSchema, 'AssignSegmentSpeaker');
            return { data: transformer(result.data) };
        },
        invalidatesTags: (_result, _error, params) => [
            { type: 'MeetingActionItems', id: params.meetingId },
            { type: 'MeetingActionItems', id: `project-${params.projectId}` },
            { type: 'MeetingAgreements', id: params.meetingId },
        ],
    }),
    deleteActionItem: builder.mutation({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/${params.meetingId}/action-items/${params.actionItemId}?project_id=${params.projectId}`;
            const result = await baseQuery({
                url,
                method: 'DELETE',
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            return { data: null };
        },
        invalidatesTags: (_result, _error, params) => [
            { type: 'MeetingActionItems', id: params.meetingId },
            { type: 'MeetingActionItems', id: `project-${params.projectId}` },
        ],
    }),
    bulkReassignActionItemsByIds: builder.mutation({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/projects/${params.projectId}/action-items/bulk-reassign-by-ids`;
            const result = await baseQuery({
                url,
                method: 'POST',
                body: mapToSnakeCase(params.body),
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            return { data: null };
        },
        invalidatesTags: (_result, _error, params) => [
            { type: 'MeetingActionItems', id: `project-${params.projectId}` },
        ],
    }),
    bulkReassignMeetingActionItemsByIds: builder.mutation({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/${params.meetingId}/action-items/bulk-reassign-by-ids?project_id=${params.projectId}`;
            const result = await baseQuery({
                url,
                method: 'POST',
                body: mapToSnakeCase(params.body),
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            return { data: null };
        },
        invalidatesTags: (_result, _error, params) => [
            { type: 'MeetingActionItems', id: params.meetingId },
            { type: 'MeetingActionItems', id: `project-${params.projectId}` },
        ],
    }),
    // Bulk update status for action items by IDs (meeting-level) - transactional, all-or-nothing
    bulkUpdateStatusMeetingActionItemsByIds: builder.mutation({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/${params.meetingId}/action-items/bulk-update-status?project_id=${params.projectId}`;
            const result = await baseQuery({
                url,
                method: 'POST',
                body: mapToSnakeCase(params.body),
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            return { data: null };
        },
        invalidatesTags: (_result, _error, params) => [
            { type: 'MeetingActionItems', id: params.meetingId },
            { type: 'MeetingActionItems', id: `project-${params.projectId}` },
        ],
    }),
    // Bulk update deadline for action items by IDs (meeting-level) - transactional, all-or-nothing
    bulkUpdateDeadlineMeetingActionItemsByIds: builder.mutation({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/${params.meetingId}/action-items/bulk-update-due-date?project_id=${params.projectId}`;
            const result = await baseQuery({
                url,
                method: 'POST',
                body: mapToSnakeCase(params.body),
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            return { data: null };
        },
        invalidatesTags: (_result, _error, params) => [
            { type: 'MeetingActionItems', id: params.meetingId },
            { type: 'MeetingActionItems', id: `project-${params.projectId}` },
        ],
    }),
    // Bulk add topic for action items by IDs (meeting-level) - transactional, all-or-nothing
    bulkAddTopicMeetingActionItemsByIds: builder.mutation({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/${params.meetingId}/action-items/bulk-update-topic?project_id=${params.projectId}`;
            const result = await baseQuery({
                url,
                method: 'POST',
                body: mapToSnakeCase(params.body),
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            return { data: null };
        },
        invalidatesTags: (_result, _error, params) => [
            { type: 'MeetingActionItems', id: params.meetingId },
            { type: 'MeetingActionItems', id: `project-${params.projectId}` },
        ],
    }),
    // Bulk update status for action items by IDs (project-level) - transactional, all-or-nothing
    bulkUpdateStatusProjectActionItemsByIds: builder.mutation({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/projects/${params.projectId}/action-items/bulk-update-status`;
            const result = await baseQuery({
                url,
                method: 'POST',
                body: mapToSnakeCase(params.body),
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            return { data: null };
        },
        invalidatesTags: (_result, _error, params) => [
            { type: 'MeetingActionItems', id: `project-${params.projectId}` },
        ],
    }),
    // Bulk update deadline for action items by IDs (project-level) - transactional, all-or-nothing
    bulkUpdateDeadlineProjectActionItemsByIds: builder.mutation({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/projects/${params.projectId}/action-items/bulk-update-due-date`;
            const result = await baseQuery({
                url,
                method: 'POST',
                body: mapToSnakeCase(params.body),
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            return { data: null };
        },
        invalidatesTags: (_result, _error, params) => [
            { type: 'MeetingActionItems', id: `project-${params.projectId}` },
        ],
    }),
    // Bulk add topic for action items by IDs (project-level) - transactional, all-or-nothing
    bulkAddTopicProjectActionItemsByIds: builder.mutation({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/projects/${params.projectId}/action-items/bulk-update-topic`;
            const result = await baseQuery({
                url,
                method: 'POST',
                body: mapToSnakeCase(params.body),
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            return { data: null };
        },
        invalidatesTags: (_result, _error, params) => [
            { type: 'MeetingActionItems', id: `project-${params.projectId}` },
        ],
    }),
    createActionItem: builder.mutation({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/${params.meetingId}/action-items?project_id=${params.projectId}`;
            const result = await baseQuery({
                url,
                method: 'POST',
                body: mapToSnakeCase(params.body),
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            const transformer = createTransformer(actionItemResponseSchema, 'CreateActionItem');
            return { data: transformer(result.data) };
        },
        invalidatesTags: (_result, _error, params) => [
            { type: 'MeetingActionItems', id: params.meetingId },
            { type: 'MeetingActionItems', id: `project-${params.projectId}` },
        ],
    }),
    getMeetingAgreements: builder.query({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/agreements/meeting/${params.meetingId}?project_id=${params.projectId}`;
            const result = await baseQuery({
                url,
                method: 'GET',
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            const transformer = createTransformer(getAgreementsResponseSchema, 'GetMeetingAgreements');
            return { data: transformer(result.data) };
        },
        providesTags: (_result, _error, params) => [
            { type: 'MeetingAgreements', id: params.meetingId },
        ],
    }),
    generateMeetingAgreements: builder.mutation({
        queryFn: async (params, queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/agreements/generate/${params.meetingId}?project_id=${params.projectId}`;
            const genResult = await baseQuery({
                url,
                method: 'POST',
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (genResult.error) {
                return { error: serializeError(genResult.error) };
            }
            const taskInit = parseTaskInitiatedResponse(genResult.data);
            if (!taskInit.ok) {
                return { error: taskInit.error };
            }
            const statusUrl = `${meetingApiBaseUrl}/tasks/${taskInit.task_id}?project_id=${params.projectId}`;
            let result;
            try {
                result = await pollTask(() => Promise.resolve(baseQuery({
                    url: statusUrl,
                    method: 'GET',
                    headers: buildMeetingSecretaryHeaders(params),
                })), generateAgreementsResponseSchema, { signal: queryApi.signal });
            }
            catch (err) {
                return { error: serializeError(err) };
            }
            return { data: result };
        },
        invalidatesTags: (_result, _error, params) => [
            { type: 'MeetingAgreements', id: params.meetingId },
        ],
    }),
    updateAgreement: builder.mutation({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/agreements/agreement/${params.agreementId}?project_id=${params.projectId}`;
            const result = await baseQuery({
                url,
                method: 'PATCH',
                body: mapToSnakeCase(params.body),
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            const transformer = createTransformer(meetingAgreementSchema, 'UpdateAgreement');
            return { data: transformer(result.data) };
        },
        invalidatesTags: (_result, _error, params) => [
            { type: 'MeetingAgreements', id: params.meetingId },
        ],
    }),
    deleteAgreement: builder.mutation({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/agreements/agreement/${params.agreementId}?project_id=${params.projectId}`;
            const result = await baseQuery({
                url,
                method: 'DELETE',
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            return { data: null };
        },
        invalidatesTags: (_result, _error, params) => [
            { type: 'MeetingAgreements', id: params.meetingId },
            { type: 'MeetingContradictions', id: params.meetingId },
        ],
    }),
    getMeetingAgreementContradictions: builder.query({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/agreement-contradictions/meeting/${params.meetingId}/contradictions?project_id=${params.projectId}`;
            const result = await baseQuery({
                url,
                method: 'GET',
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            const transformer = createTransformer(getAgreementContradictionsResponseSchema, 'GetMeetingAgreementContradictions');
            return { data: transformer(result.data) };
        },
        providesTags: (_result, _error, params) => [
            { type: 'MeetingContradictions', id: params.meetingId },
        ],
    }),
    generateMeetingAgreementContradictions: builder.mutation({
        queryFn: async (params, queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/agreement-contradictions/generate?project_id=${params.projectId}`;
            const genResult = await baseQuery({
                url,
                method: 'POST',
                body: { meeting_id: params.meetingId },
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (genResult.error) {
                return { error: serializeError(genResult.error) };
            }
            const enqueue = generateEnqueueResponseSchema.safeParse(genResult.data);
            if (!enqueue.success) {
                return {
                    error: serializeError({
                        status: 'CUSTOM_ERROR',
                        data: `Invalid agreement-contradictions/generate response: ${enqueue.error.message}`,
                    }),
                };
            }
            // Contradictions already existed — nothing to poll, the caller fetches them next.
            if (enqueue.data.already_generated) {
                return { data: null };
            }
            // No task queued and not already generated — unexpected.
            if (!enqueue.data.task_id) {
                return {
                    error: serializeError({
                        status: 'CUSTOM_ERROR',
                        data: 'agreement-contradictions/generate returned no task_id and already_generated was false',
                    }),
                };
            }
            const statusUrl = `${meetingApiBaseUrl}/tasks/${enqueue.data.task_id}?project_id=${params.projectId}`;
            try {
                await pollTask(() => Promise.resolve(baseQuery({
                    url: statusUrl,
                    method: 'GET',
                    headers: buildMeetingSecretaryHeaders(params),
                })), { parse: () => undefined }, { signal: queryApi.signal });
            }
            catch (err) {
                return { error: serializeError(err) };
            }
            return { data: null };
        },
        invalidatesTags: (_result, _error, params) => [
            { type: 'MeetingContradictions', id: params.meetingId },
        ],
    }),
    getDocumentContradictions: builder.query({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/document-contradictions/document-contradictions?project_id=${params.projectId}&meeting_id=${params.meetingId}&document_package_id=${params.documentPackageId}`;
            const result = await baseQuery({
                url,
                method: 'GET',
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            const transformer = createTransformer(getDocumentContradictionsResponseSchema, 'GetDocumentContradictions');
            return { data: transformer(result.data) };
        },
        providesTags: (_result, _error, params) => [
            { type: 'DocumentContradictions', id: `${params.meetingId}-${params.documentPackageId}` },
        ],
    }),
    generateDocumentContradictions: builder.mutation({
        queryFn: async (params, queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/document-contradictions/generate?project_id=${params.projectId}&meeting_id=${params.meetingId}&document_package_id=${params.documentPackageId}`;
            const genResult = await baseQuery({
                url,
                method: 'POST',
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (genResult.error) {
                return { error: serializeError(genResult.error) };
            }
            const fetchContradictions = async () => {
                const contradictionsUrl = `${meetingApiBaseUrl}/meetings/document-contradictions/document-contradictions?project_id=${params.projectId}&meeting_id=${params.meetingId}&document_package_id=${params.documentPackageId}`;
                const fetched = await baseQuery({
                    url: contradictionsUrl,
                    method: 'GET',
                    headers: buildMeetingSecretaryHeaders(params),
                });
                if (fetched.error) {
                    return { error: serializeError(fetched.error) };
                }
                const transformer = createTransformer(getDocumentContradictionsResponseSchema, 'GenerateDocumentContradictions');
                return { data: transformer(fetched.data) };
            };
            const enqueue = generateEnqueueResponseSchema.safeParse(genResult.data);
            if (!enqueue.success) {
                return {
                    error: serializeError({
                        status: 'CUSTOM_ERROR',
                        data: `Invalid document-contradictions/generate response: ${enqueue.error.message}`,
                    }),
                };
            }
            // Contradictions already existed — nothing to poll, fetch them directly.
            if (enqueue.data.already_generated) {
                return fetchContradictions();
            }
            // No task queued and not already generated — unexpected.
            if (!enqueue.data.task_id) {
                return {
                    error: serializeError({
                        status: 'CUSTOM_ERROR',
                        data: 'document-contradictions/generate returned no task_id and already_generated was false',
                    }),
                };
            }
            const statusUrl = `${meetingApiBaseUrl}/tasks/${enqueue.data.task_id}?project_id=${params.projectId}`;
            let result;
            try {
                result = await pollTask(() => Promise.resolve(baseQuery({
                    url: statusUrl,
                    method: 'GET',
                    headers: buildMeetingSecretaryHeaders(params),
                })), getDocumentContradictionsResponseSchema, { signal: queryApi.signal });
            }
            catch (err) {
                return { error: serializeError(err) };
            }
            return { data: result };
        },
        invalidatesTags: (_result, _error, params) => [
            { type: 'DocumentContradictions', id: `${params.meetingId}-${params.documentPackageId}` },
        ],
    }),
    createMeeting: builder.mutation({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/create-meeting-with-calendar?project_id=${params.projectId}&created_by_assistant=true`;
            const result = await baseQuery({
                url,
                method: 'POST',
                body: params.body,
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            const transformer = createTransformer(meetingSchema, 'CreateMeeting');
            return { data: transformer(result.data) };
        },
        invalidatesTags: (_result, _error, params) => [
            'Meetings',
            { type: 'Meetings', id: `statistics-${params.projectId}` },
        ],
    }),
    createRecurringSeries: builder.mutation({
        queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/recurring-series/generate-recurring-series?project_id=${params.projectId}`;
            const result = await baseQuery({
                url,
                method: 'POST',
                body: mapToSnakeCase(params.body),
                headers: buildMeetingSecretaryHeaders(params),
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            const transformer = createTransformer(recurringSeriesResponseSchema, 'CreateRecurringSeries');
            return { data: transformer(result.data) };
        },
        invalidatesTags: (_result, _error, params) => [
            'Meetings',
            { type: 'Meetings', id: `statistics-${params.projectId}` },
        ],
    }),
    syncMeetings: builder.mutation({
        queryFn: async (params, queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/sync_meetings?project_id=${params.projectId}&use_delta=${params.useDelta ?? true}`;
            const headers = buildMeetingSecretaryHeaders(params);
            const result = await baseQuery({
                url,
                method: 'POST',
                body: {
                    meeting_ids: params.meetingIds || null,
                },
                headers,
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            const enqueued = taskEnqueueResponseSchema.safeParse(result.data);
            if (!enqueued.success) {
                return { error: serializeError(new Error('Unexpected syncMeetings response')) };
            }
            const statusUrl = `${meetingApiBaseUrl}/tasks/${enqueued.data.task_id}?project_id=${params.projectId}`;
            try {
                await pollTask(() => baseQuery({ url: statusUrl, method: 'GET', headers }), { parse: () => ({}) }, {
                    signal: queryApi.signal,
                    intervalMs: 3000,
                    backoffMultiplier: 2,
                    maxIntervalMs: 30_000,
                });
            }
            catch (err) {
                return { error: serializeError(err) };
            }
            return { data: {} };
        },
        invalidatesTags: (_result, _error, params) => [
            'Meetings',
            { type: 'Meetings', id: `statistics-${params.projectId}` },
        ],
    }),
    importOutlookMeetings: builder.mutation({
        queryFn: async (params, queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/import-from-outlook?project_id=${params.projectId}`;
            const headers = buildMeetingSecretaryHeaders(params);
            const result = await baseQuery({
                url,
                method: 'POST',
                body: {
                    event_ids: params.eventIds,
                    series_ids: params.seriesIds,
                },
                headers,
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            const enqueued = taskEnqueueResponseSchema.safeParse(result.data);
            if (!enqueued.success) {
                return { error: serializeError(new Error('Unexpected importOutlookMeetings response')) };
            }
            const statusUrl = `${meetingApiBaseUrl}/tasks/${enqueued.data.task_id}?project_id=${params.projectId}`;
            try {
                const summary = await pollTask(() => baseQuery({ url: statusUrl, method: 'GET', headers }), { parse: (data) => importOutlookMeetingsResponseSchema.parse(data) }, {
                    signal: queryApi.signal,
                    intervalMs: 3000,
                    backoffMultiplier: 2,
                    maxIntervalMs: 30_000,
                });
                return { data: summary };
            }
            catch (err) {
                return { error: serializeError(err) };
            }
        },
        invalidatesTags: (_result, _error, params) => [
            'Meetings',
            { type: 'Meetings', id: `statistics-${params.projectId}` },
        ],
    }),
    autoImportTagged: builder.mutation({
        queryFn: async (params, queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/meetings/auto-import-tagged?project_id=${params.projectId}`;
            const headers = buildMeetingSecretaryHeaders(params);
            const result = await baseQuery({ url, method: 'POST', headers });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            const enqueued = taskEnqueueResponseSchema.safeParse(result.data);
            if (!enqueued.success) {
                return { error: serializeError(new Error('Unexpected autoImportTagged response')) };
            }
            const statusUrl = `${meetingApiBaseUrl}/tasks/${enqueued.data.task_id}?project_id=${params.projectId}`;
            try {
                const summary = await pollTask(() => baseQuery({ url: statusUrl, method: 'GET', headers }), { parse: (data) => autoImportTaggedResultSchema.parse(data ?? {}) }, {
                    signal: queryApi.signal,
                    intervalMs: 1000,
                    backoffMultiplier: 1.5,
                    maxIntervalMs: 4000,
                });
                return { data: summary };
            }
            catch (err) {
                return { error: serializeError(err) };
            }
        },
        invalidatesTags: (_result, _error, params) => [
            'Meetings',
            { type: 'Meetings', id: `statistics-${params.projectId}` },
        ],
    }),
    syncRecurringSeries: builder.mutation({
        queryFn: async (params, queryApi, _extraOptions, baseQuery) => {
            const url = `${meetingApiBaseUrl}/recurring-series/sync?project_id=${params.projectId}`;
            const headers = buildMeetingSecretaryHeaders(params);
            const result = await baseQuery({
                url,
                method: 'POST',
                body: {
                    series_ids: params.seriesIds,
                },
                headers,
            });
            if (result.error) {
                return { error: serializeError(result.error) };
            }
            const enqueued = z.array(taskEnqueueResponseSchema).safeParse(result.data);
            if (!enqueued.success) {
                return { error: serializeError(new Error('Unexpected syncRecurringSeries response')) };
            }
            if (enqueued.data.length > 0) {
                try {
                    await Promise.all(enqueued.data.map(({ task_id }) => {
                        const statusUrl = `${meetingApiBaseUrl}/tasks/${task_id}?project_id=${params.projectId}`;
                        return pollTask(() => baseQuery({ url: statusUrl, method: 'GET', headers }), { parse: () => ({}) }, {
                            signal: queryApi.signal,
                            intervalMs: 3000,
                            backoffMultiplier: 2,
                            maxIntervalMs: 30_000,
                        });
                    }));
                }
                catch (err) {
                    return { error: serializeError(err) };
                }
            }
            // If the backend returns no tasks there is nothing to sync — return success immediately.
            return { data: {} };
        },
        invalidatesTags: (_result, _error, params) => [
            'Meetings',
            { type: 'Meetings', id: `statistics-${params.projectId}` },
        ],
    }),
    deleteRecurringSeries: builder.mutation({
        query: (params) => ({
            url: `${meetingApiBaseUrl}/recurring-series/${params.recurringSeriesId}?project_id=${params.projectId}${params.deleteFromOutlook ? '&delete_from_outlook=true' : ''}`,
            method: 'DELETE',
            ...(params.deleteFromOutlook && params.msAccessToken
                ? {
                    headers: buildMeetingSecretaryHeaders({
                        msAccessToken: params.msAccessToken,
                        userEmail: params.userEmail ?? '',
                        userId: params.userId ?? '',
                    }),
                }
                : {}),
        }),
        invalidatesTags: (_result, _error, params) => [
            'Meetings',
            { type: 'Meetings', id: `statistics-${params.projectId}` },
        ],
    }),
    deleteMeeting: builder.mutation({
        query: (params) => ({
            url: `${meetingApiBaseUrl}/meetings/${params.meetingId}?project_id=${params.projectId}`,
            method: 'DELETE',
        }),
        invalidatesTags: (_result, _error, params) => [
            'Meetings',
            { type: 'Meetings', id: `statistics-${params.projectId}` },
        ],
    }),
    deleteMeetingCalendarEvent: builder.mutation({
        query: (params) => ({
            url: `${meetingApiBaseUrl}/meetings/${params.meetingId}/calendar-event?project_id=${params.projectId}`,
            method: 'DELETE',
            headers: buildMeetingSecretaryHeaders(params),
        }),
        invalidatesTags: (_result, _error, params) => [
            'Meetings',
            { type: 'Meetings', id: `statistics-${params.projectId}` },
        ],
    }),
    sendMeetingEmail: builder.mutation({
        query: (params) => ({
            url: `${meetingApiBaseUrl}/meetings/${params.meetingId}/send-email?project_id=${params.projectId}`,
            method: 'POST',
            body: {
                content_option: params.contentOption,
                recipient_emails: params.recipientEmails,
                language: params.language,
            },
        }),
    }),
    getProjectParticipants: builder.query({
        query: ({ projectId }) => ({
            url: `${meetingApiBaseUrl}/projects/${projectId}/participants`,
        }),
        transformResponse: createTransformer(getProjectParticipantsResponseSchema, 'GetProjectParticipants'),
        providesTags: ['MeetingParticipants'],
    }),
});
